import { useCallback, useEffect, useMemo, useState } from 'react';

import { useProducts } from '../../../hooks/useProducts';
import { useProductVariantAvailabilities } from '../../../hooks/useProductVariantAvailabilities';
import { useSessionStorage } from '../../../hooks/useSessionStorage';
import type { PrivateProductsEndpointResponse } from '../../../pages/api/private/products';
import { GeneratedPrivateLink } from '../../../types/generated';
import { AnalyticsEmitter } from '../../../utils/analytics/emitter';
import { shopifyAdminIdToGraphqlId } from '../../../utils/shopifyIds';
import {
  Options,
  PrivateLinkProduct,
  PrivateSalesController,
} from '../types/PrivateLink';

type PartialProduct = Omit<
  PrivateLinkProduct,
  'shopifyProduct' | 'availableForSale'
>;

export function usePrivateSales(options: Options): PrivateSalesController {
  const { isExpired, passwordRequired, privateSalesGateway, slug } = options;
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);
  const [partialProducts, setPartialProducts] = useState<PartialProduct[]>([]);

  const { getSessionStorage, removeSessionStorageItem, setSessionStorage } =
    useSessionStorage();

  const productIds = partialProducts.map((p) =>
    shopifyAdminIdToGraphqlId(p.shopifyProductIdentifier.id),
  );

  const productVariantIds = partialProducts.map((p) => p.shopifyVariantId);

  const {
    loading: productsLoading,
    products: shopifyProducts,
    productsRaw: shopifyProductsRaw,
  } = useProducts(productIds);

  const { availabilities, loading: availabilityLoading } =
    useProductVariantAvailabilities(productVariantIds);

  const getPrivateLinkWithProducts = useCallback(
    async (password = '', analyticsEmitter?: AnalyticsEmitter) => {
      setLoading(true);

      const { privateLink } =
        await privateSalesGateway.getPrivateLinkWithProducts(
          slug,
          password,
          analyticsEmitter,
        );

      if (privateLink) {
        setShowLoadingScreen(true);

        const nextProducts = await getAllPartialProducts(privateLink, password);
        if (nextProducts !== null) {
          setPartialProducts(nextProducts);
          setSessionStorage(privateLink.slug, password);
          setLocked(false);

          setTimeout(() => {
            setShowLoadingScreen(() => false);
          }, 1000);
        }
      } else {
        removeSessionStorageItem(slug);
        setShowLoadingScreen(false);
      }
      setLoading(false);
    },
    [privateSalesGateway, removeSessionStorageItem, setSessionStorage, slug],
  );

  useEffect(() => {
    const password = getSessionStorage(slug);

    if ((password || !passwordRequired) && !isExpired) {
      getPrivateLinkWithProducts(password);
    } else {
      setShowLoadingScreen(false);
    }
  }, [getPrivateLinkWithProducts, getSessionStorage, passwordRequired, slug]);

  const products = useMemo(() => {
    const finalProducts: PrivateLinkProduct[] = [];

    partialProducts.forEach((partialProduct) => {
      const shopifyProduct = shopifyProducts.find((p) => {
        const id = shopifyAdminIdToGraphqlId(
          partialProduct.shopifyProductIdentifier.id,
        );

        return p.id === id;
      });
      const shopifyProductRaw = shopifyProductsRaw.find((p) => {
        const id = shopifyAdminIdToGraphqlId(
          partialProduct.shopifyProductIdentifier.id,
        );

        return p.id === id;
      });
      const shopifyProductRawVariant = shopifyProductRaw?.variants.edges.find(
        (variant) => variant.node.id === partialProduct.shopifyVariantId,
      );

      const availability = availabilities.find((a) => {
        const id = partialProduct.shopifyVariantId;

        return a.id === id;
      });

      if (shopifyProduct !== undefined) {
        finalProducts.push({
          ...partialProduct,
          shopifyProduct: {
            ...shopifyProduct,
            ...(shopifyProductRawVariant && {
              price: shopifyProductRawVariant.node.price.amount as string,
            }),
          },
          availableForSale: availability?.availableForSale,
        });
      }
    });

    return finalProducts;
  }, [partialProducts, shopifyProducts, shopifyProductsRaw, availabilities]);

  return {
    getPrivateLinkWithProducts,
    loading: loading || productsLoading || availabilityLoading,
    locked,
    showLoadingScreen,
    products,
  };
}

async function getAllPartialProducts(
  privateLink: GeneratedPrivateLink,
  password: string,
): Promise<PartialProduct[]> {
  if (!privateLink.products) return [];

  const partialProducts = await Promise.all(
    privateLink.products
      .filter((p) => p.shopifyVariantId !== undefined)
      .map((p) =>
        getPartialProduct(privateLink, p.shopifyVariantId!, password),
      ),
  );

  return partialProducts.filter((p): p is PartialProduct => !!p);
}

async function getPartialProduct(
  privateLink: GeneratedPrivateLink,
  shopifyVariantId: string,
  password: string,
): Promise<PartialProduct | null> {
  try {
    const res = await fetch('/api/private/products', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        privateLinkSlug: privateLink.slug,
        password,
        shopifyVariantId,
      }),
    });

    const json = (await res.json()) as PrivateProductsEndpointResponse;

    if ('product' in json && json.product !== null) {
      const { product } = json;
      const linkProduct = privateLink.products?.find(
        (p) =>
          p.shopifyVariantId !== null &&
          p.shopifyVariantId !== undefined &&
          `gid://shopify/ProductVariant/${p.shopifyVariantId}` ===
            product.variantId,
      );

      if (linkProduct === undefined) return null;
      if (product.content === null) return null;

      return {
        shopifyVariantId: product.variantId,
        shopifyProductIdentifier:
          product.content.component === 'product_v2'
            ? ({ id: product.content.shopifyId } as ShopifyProductIdentifier)
            : (product.content.id[0] as ShopifyProductIdentifier),
        slug: product.slug,
        storyblokProduct: product.content,
        unlockCode: linkProduct.unlockCode,
        tags: linkProduct.tags,
        isEnquiry: Boolean(linkProduct.isEnquiry),
        comment: linkProduct.comment
          ? {
              body: linkProduct.comment.body,
              avatarUrl: linkProduct.comment.avatarUrl,
            }
          : undefined,
      };
    }

    return null;
  } catch (err) {
    console.error(err);
    return null;
  }
}
